import React, { useState, useEffect, useRef } from 'react';
import { FiStar, FiUsers, FiPackage, FiThumbsUp, FiExternalLink, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

// Counter animation hook
const useCounter = (end: number, start = 0, duration = 2000) => {
  const [count, setCount] = useState(start);
  const countRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!countRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    try {
      observer.observe(countRef.current);
    } catch (error) {
      console.error("IntersectionObserver error:", error);
      setIsVisible(true);
    }

    return () => {
      try {
        observer.disconnect();
      } catch (error) {
        console.error("Error disconnecting observer:", error);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    const animateCount = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const currentCount = Math.floor(progress * (end - start) + start);
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animateCount);
      }
    };

    requestAnimationFrame(animateCount);
  }, [end, start, duration, isVisible]);

  return { count, ref: countRef };
};

// Testimonial card component
interface TestimonialCardProps {
  name: string;
  rating: number;
  comment: string;
  date: string;
  image?: string;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ name, rating, comment, date }) => {
  return (
    <div 
      className="bg-background-light p-6 rounded-lg border border-gray-800/40 transition-all duration-300 h-full flex flex-col"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          {[...Array(5)].map((_, i) => (
            <FiStar 
              key={i} 
              className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'}`} 
            />
          ))}
        </div>
        <span className="text-sm text-gray-400">{date}</span>
      </div>
      <p className="text-secondary leading-relaxed mb-4 flex-grow">{comment}</p>
      <div className="flex items-center justify-between">
        <span className="font-medium text-white">{name}</span>
      </div>
    </div>
  );
};

// Carousel component for reviews
const ReviewCarousel: React.FC<{testimonials: any[]}> = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const maxIndex = Math.ceil(testimonials.length / 3) - 1;
  
  // Auto-advance carousel
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex >= maxIndex ? 0 : prevIndex + 1
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [maxIndex, isPaused]);
  
  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex >= maxIndex ? 0 : prevIndex + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex <= 0 ? maxIndex : prevIndex - 1
    );
  };
  
  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, pageIndex) => (
            <div key={pageIndex} className="grid grid-cols-1 md:grid-cols-3 gap-6 min-w-full">
              {testimonials.slice(pageIndex * 3, pageIndex * 3 + 3).map((testimonial, idx) => (
                <TestimonialCard
                  key={pageIndex * 3 + idx}
                  name={testimonial.name}
                  rating={testimonial.rating}
                  comment={testimonial.comment}
                  date={testimonial.date}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      
      {/* Carousel controls */}
      <div className="flex justify-center items-center mt-6 space-x-4">
        <button 
          onClick={prevSlide}
          className="p-2 rounded-full bg-background-light hover:bg-accent/20 text-white transition-colors duration-300"
          aria-label="Previous reviews"
        >
          <FiChevronLeft className="w-5 h-5" />
        </button>
        
        <div className="flex space-x-2">
          {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex === idx ? 'bg-accent w-6' : 'bg-gray-600'
              }`}
              aria-label={`Go to review page ${idx + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={nextSlide}
          className="p-2 rounded-full bg-background-light hover:bg-accent/20 text-white transition-colors duration-300"
          aria-label="Next reviews"
        >
          <FiChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const Reviews: React.FC = () => {
  const productsCounter = useCounter(1000);
  const soldCounter = useCounter(15400);
  const followersCounter = useCounter(86000);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    try {
      observer.observe(sectionRef.current);
    } catch (error) {
      console.error("IntersectionObserver error:", error);
      setIsVisible(true);
    }

    return () => {
      try {
        observer.disconnect();
      } catch (error) {
        console.error("Error disconnecting observer:", error);
      }
    };
  }, []);
  
  // Real Whatnot testimonials
  const testimonials = [
    {
      name: "i_love_this_app",
      rating: 5,
      comment: "5 ⭐️⭐️⭐️⭐️⭐️ seller all day. Thanks so much! You guys are the best.",
      date: "4/23/2023"
    },
    {
      name: "angels62",
      rating: 5,
      comment: "nice shoe at the right price",
      date: "4/23/2023"
    },
    {
      name: "blaze2766",
      rating: 5,
      comment: "Great shoes ty",
      date: "4/23/2023"
    },
    {
      name: "robacridge06",
      rating: 5,
      comment: "goated shoes goated seller",
      date: "4/23/2023"
    },
    {
      name: "avifedergreen",
      rating: 5,
      comment: "Great items and service",
      date: "4/23/2023"
    },
    {
      name: "shyan3255",
      rating: 5,
      comment: "I love the shoes and I've always wanted it but didn't want to pay retail and you guys always hook it up as always!!!",
      date: "4/23/2023"
    },
    {
      name: "gotemkickz25",
      rating: 5,
      comment: "Clean!!!",
      date: "4/23/2023"
    },
    {
      name: "lotis274",
      rating: 5,
      comment: "Very Grateful!",
      date: "4/23/2023"
    },
    {
      name: "jessetorres2342",
      rating: 5,
      comment: "Shipping was fast thank you for the kicks I have them on now.",
      date: "4/23/2023"
    },
    {
      name: "patriotchuck",
      rating: 5,
      comment: "Always been a great streamer and seller. Quick ship and great products. Keep getting me some 🔥🔥🔥 9.5's and I'll keep buying!",
      date: "4/23/2023"
    },
    {
      name: "martinsch",
      rating: 5,
      comment: "As described and shipped quickly. Thx!",
      date: "4/23/2023"
    },
    {
      name: "golani216",
      rating: 5,
      comment: "always a pleasure 🙏",
      date: "4/23/2023"
    },
    {
      name: "kroloff28",
      rating: 5,
      comment: "I love these boys and I love these shoes!! thanks so much!",
      date: "4/22/2023"
    },
    {
      name: "cash_like_johnny",
      rating: 5,
      comment: "Another great purchase from Grailed804! Highly recommend you check them out!",
      date: "4/22/2023"
    },
    {
      name: "cash_like_johnny",
      rating: 5,
      comment: "Great stream right here! Some of the absolute best deals that you'll find on this app!",
      date: "4/22/2023"
    },
    {
      name: "cash_like_johnny",
      rating: 5,
      comment: "Always enjoy tuning in to these guys' stream! Highly recommend!",
      date: "4/22/2023"
    },
    {
      name: "mrs_pellot",
      rating: 5,
      comment: "💯🔥",
      date: "4/22/2023"
    },
    {
      name: "craclifton",
      rating: 5,
      comment: "I happy with my purchase will buy again from them.",
      date: "4/22/2023"
    },
    {
      name: "alanpaidtomuch",
      rating: 5,
      comment: "Nice shoe fair price and shipped quickly",
      date: "4/22/2023"
    },
    {
      name: "z71truck",
      rating: 5,
      comment: "Super fast shipping! Thank you!",
      date: "4/22/2023"
    },
    {
      name: "bankco",
      rating: 5,
      comment: "Fast shipping, best prices, best giveaways!",
      date: "4/21/2023"
    },
    {
      name: "2diefour",
      rating: 5,
      comment: "fast shipping !!!!",
      date: "4/21/2023"
    },
    {
      name: "stephyglo",
      rating: 5,
      comment: "Thanks for the givy!!",
      date: "4/21/2023"
    },
    {
      name: "juumanjii23",
      rating: 5,
      comment: "These are one of my favorite sb's I've been wanting for a long time. Thanks yall for the steal price!",
      date: "4/21/2023"
    },
    {
      name: "donadrian91181",
      rating: 5,
      comment: "Thank you. Extremely happy with the shoes",
      date: "4/21/2023"
    },
    {
      name: "tammyp09",
      rating: 5,
      comment: "awesome giveaway.",
      date: "4/20/2023"
    },
    {
      name: "chopboi612",
      rating: 5,
      comment: "Great deal!",
      date: "4/20/2023"
    },
    {
      name: "bhooper63",
      rating: 5,
      comment: "Didn't think it would be a sketcher but damn these things are comfy",
      date: "4/19/2023"
    },
    {
      name: "cash_like_johnny",
      rating: 5,
      comment: "These Jordan 4s are incredible! Thanks for the awesome deal!",
      date: "4/19/2023"
    },
    {
      name: "patriotchuck",
      rating: 5,
      comment: "Always a great seller. Fast shipping and great customer service.",
      date: "4/18/2023"
    }
  ];

  return (
    <section 
      id="reviews" 
      ref={sectionRef}
      className="py-8 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/5 blur-3xl animate-float" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-accent/5 blur-3xl animate-float" style={{ animationDelay: '1.2s' }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col items-center text-center mb-8">
          <h2 className={`text-3xl font-bold tracking-tight text-primary mb-3 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            What Our Customers Say
          </h2>
          <div className={`w-20 h-1 bg-accent mb-4 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
          }`}></div>
          <p className={`max-w-2xl text-lg text-secondary transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            Real reviews from our Whatnot customers who trust us for authentic sneakers
          </p>
        </div>
        
        {/* Stats section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <div className="bg-background-light p-8 rounded-lg border border-gray-800/40 shadow-lg transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
            <div className="flex items-center mb-3">
              <div className="w-12 h-12 rounded-full bg-accent/10 text-accent flex items-center justify-center mr-4">
                <FiPackage className="w-6 h-6" />
              </div>
              <div>
                <div ref={productsCounter.ref} className="text-4xl font-bold text-accent">
                  {productsCounter.count.toLocaleString()}+
                </div>
                <p className="text-secondary">Available Products</p>
              </div>
            </div>
          </div>
          
          <div className="bg-background-light p-8 rounded-lg border border-gray-800/40 shadow-lg transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1" style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center mb-3">
              <div className="w-12 h-12 rounded-full bg-accent/10 text-accent flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <div>
                <div ref={soldCounter.ref} className="text-4xl font-bold text-accent">
                  {soldCounter.count.toLocaleString()}+
                </div>
                <p className="text-secondary">Items Sold</p>
              </div>
            </div>
          </div>
          
          <div className="bg-background-light p-8 rounded-lg border border-gray-800/40 shadow-lg transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1" style={{ transitionDelay: '200ms' }}>
            <div className="flex items-center mb-3">
              <div className="w-12 h-12 rounded-full bg-accent/10 text-accent flex items-center justify-center mr-4">
                <FiUsers className="w-6 h-6" />
              </div>
              <div>
                <div ref={followersCounter.ref} className="text-4xl font-bold text-accent">
                  {followersCounter.count.toLocaleString()}+
                </div>
                <p className="text-secondary">Whatnot Followers</p>
              </div>
            </div>
          </div>
          
          <div className="bg-background-light p-8 rounded-lg border border-gray-800/40 shadow-lg transform transition-all duration-500 hover:shadow-xl hover:-translate-y-1" style={{ transitionDelay: '300ms' }}>
            <div className="flex items-center mb-3">
              <div className="w-12 h-12 rounded-full bg-accent/10 text-accent flex items-center justify-center mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent">
                  2 Days
                </div>
                <p className="text-secondary">Average Ship Time</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Reviews call-to-action */}
        <div className={`bg-gradient-to-r from-accent/20 to-purple-900/20 rounded-lg p-8 mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`} style={{ transitionDelay: '300ms' }}>
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0 md:mr-8">
              <h3 className="text-2xl font-bold mb-4 text-white">See All Reviews on Whatnot</h3>
              <p className="text-gray-300 mb-4">Check out our profile on Whatnot to see all customer reviews and join our live shows.</p>
            </div>
            <a 
              href="https://www.whatnot.com/user/grailed804/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-accent hover:bg-accent/80 text-white font-medium rounded-md transition-colors flex items-center whitespace-nowrap"
            >
              View All Reviews
              <FiExternalLink className="ml-2" />
            </a>
          </div>
        </div>
        
        {/* Testimonials Carousel */}
        <div className={`transition-all duration-700 delay-400 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <ReviewCarousel testimonials={testimonials} />
        </div>
      </div>
    </section>
  );
};

export default Reviews; 